import { useCallback, useState } from "react";

import MuiDialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import BoxFolderPicker, {
  FolderObject
} from "components/folder-picker/folder-picker.component";

import type { SaveToEdocsDialogProps } from "./save-to-edocs-dialog.component";

/**
 * SAVE_TO_EDOCS_NAMING_FEATURE off: what each surface showed before SN-20071. The bare Box folder
 * picker, uploading under the surface's name at the moment of the pick, or (`legacyNameField`) the
 * plain "Document Name" field `useSaveToEdocs` had above it, handing over what was typed as it is
 * (the hook adds ".pdf" and falls back to the document's own name). Delete with the flag.
 */
export default function LegacySaveToEdocsDialog({
  open,
  folderId,
  token,
  suggestedFileName,
  documentCount = 1,
  onChoose,
  onClose,
  legacyNameField = false
}: SaveToEdocsDialogProps) {
  const [typedName, setTypedName] = useState("");
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setTypedName(suggestedFileName);
  }
  const isBatch = documentCount > 1;

  const handleChoose = useCallback(
    (folder: FolderObject | null) =>
      onChoose(
        folder,
        legacyNameField ? (isBatch ? "" : typedName) : suggestedFileName
      ),
    [isBatch, legacyNameField, onChoose, suggestedFileName, typedName]
  );

  const picker =
    folderId && token ? (
      <BoxFolderPicker
        folderId={folderId}
        token={token}
        setSelectedFolder={handleChoose}
        onClose={onClose}
      />
    ) : null;

  return (
    <MuiDialog
      fullWidth
      maxWidth="md"
      hideBackdrop
      onClose={onClose}
      open={open}
      sx={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      {legacyNameField ? (
        <DialogContent sx={{ padding: 2 }}>
          {isBatch ? (
            <Typography variant="body2" color="text.secondary">
              {documentCount} documents will be saved to the chosen folder, each
              under its own name.
            </Typography>
          ) : (
            <TextField
              fullWidth
              label="Document Name"
              value={typedName}
              onChange={(event) => setTypedName(event.target.value)}
              placeholder="Enter document name..."
              variant="outlined"
            />
          )}
          {picker}
        </DialogContent>
      ) : (
        picker
      )}
    </MuiDialog>
  );
}
